import { useMemo, useState } from "react";
import { ArrowLeft, History, Loader2, Play } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { useRecentRuns, useRunDetail } from "@/hooks/multi-agent";
import { sanitizeArtifactLabel, sanitizeArtifactMarkdown } from "@/services/multi-agent/artifactDisplay";
import type { RunSessionHistoryItem } from "@/services/multi-agent";
import { FINAL_STATUSES } from "@/constants/multi-agent/status.constants";
import "@/components/organisms/studio/streamdownOverrides.css";

function fmtDuration(start: string | null, end: string | null): string {
  if (!start || !end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

function runTime(iso: string | null): number {
  if (!iso) return 0;
  const ts = new Date(iso).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

export function RunsPage() {
  const navigate = useNavigate();
  const { sessions, isLoading: loading } = useRecentRuns();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const { detail, isLoading: detailLoading } = useRunDetail(selectedRunId);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return sessions.filter((session) => {
      if (filterStatus !== "all" && session.status !== filterStatus) return false;
      const q = search.trim().toLowerCase();
      if (q && !session.title.toLowerCase().includes(q)
        && !session.runs.some((run) => run.user_intent.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [sessions, filterStatus, search]);

  const conversationReports = useMemo(() => {
    if (!detail) return [];
    const priorRuns = Array.isArray(detail.session_runs) ? detail.session_runs : [];
    const latest = {
      runId: detail.run.id,
      intent: detail.run.user_intent,
      finalMarkdown: sanitizeArtifactMarkdown(detail.final_markdown),
      lane: detail.run.lane,
      status: detail.run.status,
      startedAt: detail.run.started_at,
      finishedAt: detail.run.finished_at,
    };
    const seen = new Set<string>();
    return [...priorRuns, latest]
      .filter((run) => {
        if (!run.runId || seen.has(run.runId)) return false;
        seen.add(run.runId);
        return true;
      })
      .sort((a, b) => runTime(a.startedAt) - runTime(b.startedAt));
  }, [detail]);

  const openSession = (session: RunSessionHistoryItem) => {
    setSelectedSessionId(session.id);
    setSelectedRunId(session.latestRunId);
  };

  const openInChat = (sessionId: string) => {
    // Session-based URL (don't expose the run id) — like dev-agent.
    navigate(`/weave?session=${sessionId}`);
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] gap-3 p-3">
      <div className="w-full md:w-[480px] md:shrink-0 term-window flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Link to="/weave" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <History className="h-4 w-4 text-[hsl(var(--primary))]" />
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              session history ({filtered.length}/{sessions.length})
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="search sessions…"
            className="flex-1 rounded-md border border-border bg-card px-2 py-1 font-mono text-[11px] outline-none focus:border-[hsl(var(--primary))]"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-md border border-border bg-card px-2 py-1 font-mono text-[11px]"
          >
            <option value="all">all</option>
            <option value="done">done</option>
            <option value="failed">failed</option>
            <option value="running">running</option>
            <option value="awaiting_approval">awaiting</option>
          </select>
        </div>
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-4 py-6 text-center font-mono text-xs text-muted-foreground">
              no sessions match.
            </div>
          )}
          <div className="divide-y divide-border">
            {filtered.map((session) => {
              const isLive = !FINAL_STATUSES.has(session.status);
              return (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => openSession(session)}
                  className={`flex w-full flex-col gap-1 px-3 py-2 text-left transition hover:bg-muted/40 ${
                    selectedSessionId === session.id ? "bg-muted/60" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="line-clamp-2 flex-1 font-mono text-[11px] text-foreground/90">
                      {session.title}
                    </span>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0 font-mono text-[9px] ${
                        session.status === "done" ? "bg-emerald-500/15 text-emerald-700"
                        : session.status === "failed" ? "bg-red-500/15 text-red-700"
                        : isLive ? "bg-[hsl(var(--accent-glow)/0.15)] text-[hsl(var(--accent-glow))]"
                        : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {session.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between font-mono text-[10px] text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <span>{session.run_count} run{session.run_count === 1 ? "" : "s"}</span>
                      <span>·</span>
                      <span>{session.lane || "free"}</span>
                      <span>·</span>
                      <span>{session.node_count} nodes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {session.cost_usd > 0 && <span>${session.cost_usd.toFixed(3)}</span>}
                      <span>{fmtDate(session.updated_at)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0 term-window flex flex-col">
        {!selectedSessionId && (
          <div className="flex flex-1 items-center justify-center font-mono text-xs text-muted-foreground">
            select a session to view its conversation runs + latest result.
          </div>
        )}
        {selectedSessionId && detailLoading && (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
        {detail && (
          <>
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <div className="flex flex-col">
                <span className="font-mono text-xs text-foreground">
                  {detail.run.user_intent}
                </span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {detail.run.lane || "free"} · {detail.event_count} events ·
                  {" "}{fmtDuration(detail.run.started_at, detail.run.finished_at)}
                  {detail.cost && (
                    <> · ${detail.cost.usedUsd.toFixed(3)} · {detail.cost.toolCalls} tool calls</>
                  )}
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInChat(detail.run.session_id || detail.run.id)}
                className="studio-button"
                title="Open session in chat view"
              >
                <Play className="h-3.5 w-3.5" />
                open session
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <div className="border-b border-border px-4 py-3">
                <div className="studio-eyebrow mb-3 text-[10px]">
                  session conversation ({conversationReports.length} run{conversationReports.length === 1 ? "" : "s"})
                </div>
                <div className="space-y-5">
                  {conversationReports.map((run, index) => (
                    <section key={run.runId} className="rounded-md border border-border bg-card/50 px-3 py-3">
                      <div className="mb-2 flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
                        <span>run #{index + 1}</span>
                        <span className="rounded bg-muted px-1.5 py-0 text-[9px]">{run.status}</span>
                        <span>{run.lane || "free"}</span>
                        <span className="ml-auto">{fmtDate(run.startedAt)}</span>
                      </div>
                      <div className="mb-3 font-mono text-[12px] font-semibold text-foreground">
                        {run.intent}
                      </div>
                      {run.finalMarkdown.trim() ? (
                        <div className="streamdown-overrides font-mono text-[12px]">
                          <SkillResultMarkdown content={sanitizeArtifactMarkdown(run.finalMarkdown)} />
                        </div>
                      ) : (
                        <div className="font-mono text-[11px] text-muted-foreground">
                          no synthesized markdown for this run.
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              </div>

              {detail.nodes.length > 0 && (
                <div className="border-b border-border px-4 py-3">
                  <div className="studio-eyebrow mb-2 text-[10px]">nodes ({detail.nodes.length})</div>
                  <div className="space-y-1">
                    {detail.nodes.map((n) => (
                      <div key={n.id} className="flex items-center gap-2 font-mono text-[10px]">
                        <span
                          className={`inline-block h-1.5 w-1.5 rounded-full ${
                            n.status === "done" ? "bg-emerald-500"
                            : n.status === "failed" ? "bg-red-500"
                            : n.status === "running" || n.status === "tool_calling" ? "bg-[hsl(var(--accent-glow))]"
                            : "bg-muted-foreground/40"
                          }`}
                        />
                        <span className="text-muted-foreground">[{n.role}]</span>
                        <span className="flex-1 truncate text-foreground/80">{n.task}</span>
                        <span
                          className={`text-[9px] ${
                            n.status === "done" ? "text-emerald-600"
                            : n.status === "failed" ? "text-red-600"
                            : "text-muted-foreground"
                          }`}
                        >
                          {n.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.partial_findings.length > 0 && (
                <div className="border-b border-border px-4 py-3">
                  <div className="studio-eyebrow mb-2 text-[10px]">findings ({detail.partial_findings.length})</div>
                  <div className="space-y-1">
                    {detail.partial_findings.map((f, i) => (
                      <div key={i} className="font-mono text-[10px]">
                        <span className="text-muted-foreground">[{f.role}]</span>{" "}
                        <span className="text-foreground/80">{f.summary}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.proposed_fixes.length > 0 && (
                <div className="border-b border-border px-4 py-3">
                  <div className="studio-eyebrow mb-2 text-[10px]">proposed fixes ({detail.proposed_fixes.length})</div>
                  <div className="space-y-1">
                    {detail.proposed_fixes.map((f, i) => (
                      <div key={i} className="font-mono text-[10px]">
                        <span className="text-muted-foreground">[{f.kind} · {f.risk}]</span>{" "}
                        <span className="text-foreground/90">{f.target}</span>{" "}
                        <span className="text-foreground/70">— {f.summary}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.artifacts.length > 0 && (
                <div className="border-b border-border px-4 py-3">
                  <div className="studio-eyebrow mb-2 text-[10px]">artifacts ({detail.artifacts.length})</div>
                  <div className="space-y-1">
                    {detail.artifacts.map((a) => (
                      <div key={a.id} className="font-mono text-[10px]">
                        <span className="text-muted-foreground">[{a.tool_name}]</span>{" "}
                        <span className="text-foreground/80">{sanitizeArtifactLabel(a.name, a.id.slice(0, 6))}</span>{" "}
                        <span className="text-muted-foreground/70">#{a.id.slice(0, 6)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
